// Shared public-site footer. Used on every standalone public page
// (homepage, /compare-all, /rates/:slug, /blog, /blog/:slug, /privacy,
// /terms). Embed mode hides it because hosts (e.g. WordPress) supply
// their own footer.
//
// Trust-signal upgrade (May 2026 — in response to Google Safe Browsing
// "Deceptive pages" flag): the footer now exposes the broker identity,
// RECA license disclosure, contact email + phone, brokerage address,
// independent-broker statement, and links to /privacy and /terms.
// These are the standard signals Google's classifier looks for when
// deciding whether a financial-services site collecting personal info
// is legitimate or phishing-adjacent.
import React from "react";

const CONTACT = {
  email: "sayhello@hellomortgage.ca",
  phone: "780-900-2495",
  brokerage_address: "500 4th Avenue SW, Suite 2500, Calgary, AB T2P 2V6",
};

export default function PublicFooter({ className = "" }) {
  const year = new Date().getFullYear();
  return (
    <footer
      className={`pt-10 mt-12 border-t border-slate-200 text-xs text-slate-500 font-body ${className}`}
      data-testid="public-footer"
    >
      <div className="max-w-3xl mx-auto space-y-5 text-center">
        {/* Disclosure — Hello Mortgage Team brand statement + BRX
            licensing affiliation. Trust signal for the Safe Browsing
            classifier (independent legitimate broker, not phishing). */}
        <div
          className="text-[12px] leading-relaxed text-slate-600 max-w-2xl mx-auto space-y-2"
          data-testid="public-footer-disclosure"
        >
          <p>
            Alberta-based, <strong>Hello Mortgage Team</strong> blends
            award-winning education, powerful technology, and legendary service
            to deliver a radically different mortgage experience for clients
            and referral partners across Alberta — one that truly makes it the
            easiest way to mortgage.
          </p>
          <p>
            Hello Mortgage Team is licensed under{" "}
            <a
              href="https://www.brxmortgage.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-brand-navy hover:text-brand-red transition-colors"
              data-testid="public-footer-brx-link"
            >
              BRX Mortgage
            </a>
            , Canada's Fastest Growing Mortgage Brokerage.
          </p>
        </div>

        {/* Contact row — visible email/phone is a phishing-classifier
            negative signal (legit brokers publish how to reach them). */}
        <div
          className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[12px] text-slate-600"
          data-testid="public-footer-contact"
        >
          <a
            href={`mailto:${CONTACT.email}`}
            className="font-medium text-brand-navy hover:text-brand-red transition-colors"
            data-testid="public-footer-email"
          >
            {CONTACT.email}
          </a>
          <span className="text-slate-300" aria-hidden="true">·</span>
          <a
            href={`tel:+1${CONTACT.phone.replace(/\D/g, "")}`}
            className="font-medium text-brand-navy hover:text-brand-red transition-colors"
            data-testid="public-footer-phone"
          >
            {CONTACT.phone}
          </a>
          <span className="text-slate-300" aria-hidden="true">·</span>
          <span data-testid="public-footer-address">
            {CONTACT.brokerage_address}
          </span>
        </div>

        {/* Legal links */}
        <div
          className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[12px]"
          data-testid="public-footer-legal-links"
        >
          <a
            href="/privacy"
            className="font-medium text-brand-navy hover:text-brand-red transition-colors"
            data-testid="public-footer-privacy-link"
          >
            Privacy Policy
          </a>
          <span className="text-slate-300" aria-hidden="true">·</span>
          <a
            href="/terms"
            className="font-medium text-brand-navy hover:text-brand-red transition-colors"
            data-testid="public-footer-terms-link"
          >
            Terms of Service
          </a>
          <span className="text-slate-300" aria-hidden="true">·</span>
          <a
            href="https://www.hellomortgage.ca/"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-brand-navy hover:text-brand-red transition-colors"
            data-testid="public-footer-hello-mortgage-link"
          >
            hellomortgage.ca
          </a>
        </div>

        {/* Compliance + copyright */}
        <div
          className="text-[11px] text-slate-400 space-y-1"
          data-testid="public-footer-compliance"
        >
          <div data-testid="public-footer-disclaimer">
            Rates are subject to change without notice. OAC. E&amp;OE.
          </div>
          <div data-testid="public-footer-copyright">
            © {year} Hello Mortgage · Calgary, Alberta · powered by BRX Mortgage
          </div>
        </div>
      </div>
    </footer>
  );
}
